import { useApiMutation, useApiPut, useApiQuery } from './useApi';
import { authService, LoginDto, RegisterDto, LoginResponse } from '@/lib/api/services/auth';
import { clearTokens, getAccessToken } from '@/lib/utils/auth';
import { useQueryClient } from '@tanstack/react-query';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
};

// Login mutation
export function useLogin(options?: {
  onSuccess?: (data: LoginResponse) => void;
  onError?: (error: any) => void;
  showErrorToast?: boolean;
}) {
  const queryClient = useQueryClient();
  return useApiMutation<LoginResponse, LoginDto>(
    '/auth/login',
    {
      mutationFn: async (variables: LoginDto) => {
        const data = await authService.login(variables);
        return { code: 200, message: 'Đăng nhập thành công', data, success: true };
      },
      retry: false,
      onSuccess: (data) => {
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('logging_out');
        }
        queryClient.invalidateQueries({ queryKey: authKeys.all as unknown as any[] });
        options?.onSuccess?.(data.data);
      },
      onError: (error) => {
        options?.onError?.(error);
      },
      showErrorToast: options?.showErrorToast,
    }
  );
}

// Register mutation
export function useRegister(options?: {
  onSuccess?: (data: LoginResponse) => void;
  onError?: (error: any) => void;
}) {
  return useApiMutation<LoginResponse, RegisterDto>(
    '/auth/register',
    {
      mutationFn: async (variables: RegisterDto) => {
        const data = await authService.register(variables);
        return { code: 200, message: 'Đăng ký thành công', data, success: true };
      },
      onSuccess: (data) => {
        options?.onSuccess?.(data.data);
      },
      onError: (error) => {
        options?.onError?.(error);
      },
    }
  );
}

// Quên mật khẩu - gửi OTP qua Zalo
export function useRequestPasswordResetOtp(options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) {
  return useApiMutation<{ ok: boolean }, { phone: string }>(
    '/auth/forgot-password/request-otp',
    {
      mutationFn: async ({ phone }) => {
        const data = await authService.requestPasswordResetOtp(phone);
        return { code: 200, message: 'Đã gửi mã OTP', data, success: true };
      },
      retry: false,
      onSuccess: () => {
        options?.onSuccess?.();
      },
      onError: (error) => {
        options?.onError?.(error);
      },
      showErrorToast: true,
    }
  );
}

// Quên mật khẩu - xác thực OTP + đặt lại mật khẩu
export function useResetPassword(options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) {
  return useApiMutation<{ ok: boolean }, { phone: string; otp: string; new_password: string }>(
    '/auth/forgot-password/reset',
    {
      mutationFn: async ({ phone, otp, new_password }) => {
        const data = await authService.resetPassword(phone, otp, new_password);
        return { code: 200, message: 'Đặt lại mật khẩu thành công', data, success: true };
      },
      retry: false,
      onSuccess: () => {
        options?.onSuccess?.();
      },
      onError: (error) => {
        options?.onError?.(error);
      },
      showErrorToast: true,
    }
  );
}

// Logout mutation
export function useLogout() {
  const queryClient = useQueryClient();
  return useApiMutation<void, void>(
    '/auth/logout',
    {
      mutationFn: async () => {
        try {
          await authService.logout();
        } catch { }
        return { code: 200, message: 'Đăng xuất thành công', data: undefined, success: true };
      },
      onSettled: () => {
        // Xóa token khỏi localStorage và cookie
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('logging_out', 'true');
          clearTokens();
        }
        queryClient.clear();
      },
      showErrorToast: false,
    }
  );
}

// Get current user
export function useCurrentUser() {
  return useApiQuery<LoginResponse['user']>(
    authKeys.me() as unknown as string[],
    '/me',
    {
      queryFn: async () => {
        const data = await authService.getCurrentUser();
        return { code: 200, message: 'Success', data, success: true };
      },
      retry: false,
      staleTime: Infinity, // Prevent refetching on error loop
      refetchInterval: false,
      enabled: !!getAccessToken(),
    }
  );
}

export function useUpdateMe(options?: {
  onSuccess?: (data: LoginResponse['user']) => void;
  onError?: (error: any) => void;
}) {
  const queryClient = useQueryClient();
  return useApiPut<LoginResponse['user'], { full_name?: string; old_password?: string; password?: string; avatar_url?: string }>('/me', {
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.me() as unknown as any[], (prev: any) => {
        if (prev && typeof prev === 'object' && 'data' in prev) {
          return { ...prev, data: data.data };
        }
        return data;
      });
      queryClient.invalidateQueries({ queryKey: authKeys.me() as unknown as any[] });
      options?.onSuccess?.(data.data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
